import GlobalVariables from "../models/globalVariables";

export const DOCUMENT_PORTAL_AUTH_NAMESPACE = "addy:document-portal-auth";
export const DOCUMENT_PORTAL_AUTH_VERSION = 1;
export const DOCUMENT_PORTAL_AUTH_REQUEST = `${DOCUMENT_PORTAL_AUTH_NAMESPACE}:request`;
export const DOCUMENT_PORTAL_AUTH_RESPONSE = `${DOCUMENT_PORTAL_AUTH_NAMESPACE}:response`;

export const LOCAL_BROWSER_AUTH_NAMESPACE = "addy:local-browser-auth";
export const LOCAL_BROWSER_AUTH_VERSION = 1;
export const LOCAL_BROWSER_AUTH_REQUEST = `${LOCAL_BROWSER_AUTH_NAMESPACE}:request`;
export const LOCAL_BROWSER_AUTH_RESPONSE = `${LOCAL_BROWSER_AUTH_NAMESPACE}:response`;

const DEFAULT_TIMEOUT_MS = 5000;
const DEFAULT_ATTEMPTS = 2;

export function getTrustedParentOrigin({
  referrer = document.referrer,
  clientDomains = GlobalVariables.getClientDomain(),
} = {}) {
  if (!referrer) {
    return null;
  }

  let referrerOrigin;
  try {
    referrerOrigin = new URL(referrer).origin;
  } catch {
    return null;
  }

  const trustedOrigins = new Set(
    clientDomains.flatMap((domain) => {
      try {
        return [new URL(domain).origin];
      } catch {
        return [];
      }
    }),
  );

  return trustedOrigins.has(referrerOrigin) ? referrerOrigin : null;
}

function createRequestId(cryptoObject) {
  if (!cryptoObject?.randomUUID) {
    throw new Error("Secure document portal authentication is unavailable.");
  }

  return cryptoObject.randomUUID();
}

function requestTokenOnce({
  targetWindow,
  parentWindow,
  parentOrigin,
  forceRefresh,
  timeoutMs,
  cryptoObject,
  requestType = DOCUMENT_PORTAL_AUTH_REQUEST,
  responseType = DOCUMENT_PORTAL_AUTH_RESPONSE,
  version = DOCUMENT_PORTAL_AUTH_VERSION,
  unavailableError = "Document portal authentication is unavailable.",
  timeoutError = "Document portal authentication timed out.",
}) {
  return new Promise((resolve, reject) => {
    const requestId = createRequestId(cryptoObject);
    let timeoutId;

    const cleanup = () => {
      targetWindow.removeEventListener("message", handleMessage);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
    const handleMessage = (event) => {
      if (event.source !== parentWindow || event.origin !== parentOrigin) {
        return;
      }

      const data = event.data;
      if (
        data?.type !== responseType ||
        data?.version !== version ||
        data?.requestId !== requestId
      ) {
        return;
      }

      cleanup();
      if (data.ok === true && typeof data.token === "string" && data.token) {
        resolve(data.token);
        return;
      }

      reject(new Error(unavailableError));
    };

    targetWindow.addEventListener("message", handleMessage);
    timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error(timeoutError));
    }, timeoutMs);

    parentWindow.postMessage(
      {
        type: requestType,
        version,
        requestId,
        forceRefresh,
      },
      parentOrigin,
    );
  });
}

export async function resolveEmbeddedSidebarIdToken({
  urlToken = "",
  requestIdToken = requestLocalBrowserIdToken,
} = {}) {
  if (typeof urlToken === "string" && urlToken.trim()) {
    return urlToken.trim();
  }

  return requestIdToken({ forceRefresh: true });
}

export async function requestDocumentPortalIdToken({
  forceRefresh = true,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  attempts = DEFAULT_ATTEMPTS,
  targetWindow = window,
  parentWindow = window.parent,
  referrer = document.referrer,
  clientDomains = GlobalVariables.getClientDomain(),
  cryptoObject = window.crypto,
} = {}) {
  if (!parentWindow || parentWindow === targetWindow) {
    throw new Error("Document portal authentication requires a parent window.");
  }

  const parentOrigin = getTrustedParentOrigin({ referrer, clientDomains });
  if (!parentOrigin) {
    throw new Error("Document portal parent origin is not trusted.");
  }

  let lastError;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await requestTokenOnce({
        targetWindow,
        parentWindow,
        parentOrigin,
        forceRefresh,
        timeoutMs,
        cryptoObject,
      });
    } catch (error) {
      lastError = error;
    }
  }

  throw (
    lastError || new Error("Document portal authentication is unavailable.")
  );
}

export async function requestLocalBrowserIdToken({
  forceRefresh = true,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  attempts = DEFAULT_ATTEMPTS,
  targetWindow = window,
  parentWindow = window.parent,
  referrer = document.referrer,
  clientDomains = GlobalVariables.getClientDomain(),
  cryptoObject = window.crypto,
} = {}) {
  if (!parentWindow || parentWindow === targetWindow) {
    throw new Error("Local browser authentication requires a parent window.");
  }

  const parentOrigin = getTrustedParentOrigin({ referrer, clientDomains });
  if (!parentOrigin) {
    throw new Error("Local browser parent origin is not trusted.");
  }

  let lastError;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await requestTokenOnce({
        targetWindow,
        parentWindow,
        parentOrigin,
        forceRefresh,
        timeoutMs,
        cryptoObject,
        requestType: LOCAL_BROWSER_AUTH_REQUEST,
        responseType: LOCAL_BROWSER_AUTH_RESPONSE,
        version: LOCAL_BROWSER_AUTH_VERSION,
        unavailableError: "Local browser authentication is unavailable.",
        timeoutError: "Local browser authentication timed out.",
      });
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error("Local browser authentication is unavailable.");
}
